import { WS_MESSAGE_TYPE, WsMessageType } from './ws';
/**
 * Shared constants for the async event-chain completion feature (KC-6440/KC-6441/KC-6442).
 *
 * Blackrik's ChainCounter fires `onEventChainComplete` once a correlation chain's last event has
 * been processed across all services. The service that fires it PUBLISHes to a single global Redis
 * channel; the backend SUBSCRIBEs and pushes the result over WebSocket to the waiting client. It
 * also writes a short-lived `chain:done:{corr}` marker so a client that subscribes *after* the chain
 * finished still gets notified.
 */

/** Global Redis pub/sub channel every service publishes chain completion to; the backend subscribes. */
export const EVENT_CHAIN_DONE_CHANNEL = 'chain-done';

/** Key prefix for the short-lived "chain already finished" marker (read by late WS subscribers). */
export const EVENT_CHAIN_DONE_KEY_PREFIX = 'chain:done:';

/** TTL (seconds) for the `chain:done` marker — long enough to bridge the command→subscribe round trip. */
export const EVENT_CHAIN_DONE_TTL_SECONDS = 60;

export const EVENT_CHAIN_STATUS = {
    COMPLETED: 'completed',
    FAILED: 'failed',
    TIMEOUT: 'timeout',
} as const;

export type EventChainStatus = (typeof EVENT_CHAIN_STATUS)[keyof typeof EVENT_CHAIN_STATUS];

/**
 * Payload sent over the pub/sub channel and forwarded to the client. Intentionally minimal and free
 * of any event payload or personal data — only the correlation id, a status, and how many events
 * failed (never the failure reasons).
 */
export type EventChainDoneMessage = {
    type: Extract<WsMessageType, typeof WS_MESSAGE_TYPE.EVENT_CHAIN>;
    correlationId: string;
    failedCount?: number;
    status: EventChainStatus;
};

/**
 * Redis key holding the completion marker for a correlation chain. The correlation id is wrapped in a
 * hash tag so the key stays on a single cluster slot.
 */
export const eventChainDoneKey = (correlationId: string) => `${EVENT_CHAIN_DONE_KEY_PREFIX}{${correlationId}}`;
